import { Typography } from '@euler/app/components/typography';
import { formatDateTime } from '@euler/utils/formatters';
import { ReactElement, useState } from 'react';
import { View } from 'react-native';
import { useBoardUnit } from '../../functions/useBoardUnit';
import { usePaged } from '../../functions/usePaged';
import { Refreshing } from '../../functions/useRefreshing';
import { workshopBoardColors as colors } from '../../workshopBoardColors';
import { SkeletonRows } from './SkeletonRows';
import { TableHeader } from './TableHeader';
import { BoardColumns, kBoardRowHeight } from './boardColumns';

/**
 * A board's rows, a screenful at a time: as many as the space measured
 * below the header holds, turning to the next page by itself. Under them,
 * how many there are, when they were loaded, and the page showing.
 */
export function BoardTable<T>({
  rows,
  columns,
  renderRow,
  emptyText,
  failedText,
  countText,
}: {
  rows: Refreshing<T[]>;
  columns: BoardColumns;
  renderRow: (item: T) => ReactElement;
  emptyText: string;
  failedText: string;
  countText: (items: T[]) => string;
}) {
  const u = useBoardUnit();
  const [bodyHeight, setBodyHeight] = useState(0);
  // Measured: none until laid out, then never less than a row.
  const perPage =
    bodyHeight > 0
      ? Math.max(1, Math.floor(bodyHeight / u(kBoardRowHeight)))
      : 0;
  const ready = rows.kind === 'ready' ? rows : null;
  const paged = usePaged(ready?.value ?? [], perPage);
  const notice =
    rows.kind === 'failed'
      ? failedText
      : ready?.value.length === 0
      ? emptyText
      : null;
  const footer = [
    ready?.value.length ? countText(ready.value) : null,
    ready ? `更新于 ${formatDateTime(ready.updatedAt, 'HH:mm')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <View style={{ flex: 1 }}>
      <TableHeader columns={columns} />
      <View
        style={{ flex: 1, overflow: 'hidden' }}
        onLayout={event => setBodyHeight(event.nativeEvent.layout.height)}
      >
        {notice ? (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography.Text size={u(28)} color={colors.muted}>
              {notice}
            </Typography.Text>
          </View>
        ) : ready ? (
          paged.items.map(renderRow)
        ) : (
          <SkeletonRows columns={columns} count={perPage} />
        )}
      </View>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          paddingTop: u(14),
        }}
      >
        <Typography.Text size={u(20)} color={colors.muted}>
          {footer || ' '}
        </Typography.Text>
        {paged.pageCount > 1 ? (
          <Typography.Text
            size={u(20)}
            color={colors.muted}
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {`${paged.page + 1} / ${paged.pageCount}`}
          </Typography.Text>
        ) : null}
      </View>
    </View>
  );
}
